import type { WorkspaceRole } from "@minga/protocol"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { AuthCallError, authClient, unwrap } from "@/lib/auth-client"
import { emailAvailabilityQuery } from "@/lib/email-api"
import { sessionQuery } from "@/lib/queries"

/** Where the verify link lands after Better Auth marks the address verified (the email's link uses the same). */
export const VERIFY_CALLBACK_URL = "/?verified=1"

/**
 * Decision 0166 §5: the `?verified=1` the verify link lands with, kept through `/` to `/welcome` (which says
 * "Verified"); anything else is dropped.
 */
export const verifiedSearch = (search: Record<string, unknown>): { verified?: "1" } =>
  search.verified === "1" || search.verified === 1 ? { verified: "1" } : {}

/**
 * Wave E (decision 0063, spec §8): the banner's sentence for an unverified address. Decision 0130 (audit item 12): a
 * member doesn't email invites, so theirs is about their account.
 */
export const verifyBannerText = (email: string, role?: WorkspaceRole): string =>
  role === "member"
    ? `Verify ${email} to keep your account secure.`
    : `Verify ${email} to email invites and see invitations sent to you.`

/** Whether the banner shows: the address is known to be unverified and this Minga can send email. */
export const showVerifyBanner = (
  user: { readonly emailVerified?: boolean | null | undefined } | null | undefined,
  emailEnabled: boolean,
): boolean => emailEnabled && user?.emailVerified === false

/** "Send the link again" failed: 429 is Better Auth's limit (3 a minute); anything else, plainly. */
export const resendVerifyErrorText = (status: number | null | undefined): string =>
  status === 429 ? "Too many requests. Try again in a minute." : "That didn't work. Try again."

/**
 * Wave E (spec §8): a quiet line under the workspace bar while the signed-in address is unverified. Signing in never
 * needs it; emailing invites, seeing invitations sent to you and being made an admin do.
 */
export function VerifyBanner({ role }: { role?: WorkspaceRole } = {}) {
  const user = useQuery(sessionQuery).data?.user
  // asked only while unverified: a verified person never pays for the request
  const emailEnabled =
    useQuery({ ...emailAvailabilityQuery, enabled: user?.emailVerified === false }).data?.enabled === true
  const send = useMutation({
    mutationFn: async () => {
      if (!user) return
      unwrap(await authClient.sendVerificationEmail({ email: user.email, callbackURL: VERIFY_CALLBACK_URL }))
    },
  })
  if (!user || !showVerifyBanner(user, emailEnabled)) return null
  const status = send.error instanceof AuthCallError ? send.error.status : null
  return (
    <div
      data-verify-banner=""
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-rule bg-fill-soft px-4 py-2 text-[13px] text-ink-2 sm:px-7"
    >
      <span className="min-w-0 break-words">{verifyBannerText(user.email, role)}</span>
      {send.isSuccess ? (
        <span className="text-muted">Sent. Check your inbox.</span>
      ) : (
        <Button size="sm" variant="secondary" disabled={send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? "Sending…" : "Send the link again"}
        </Button>
      )}
      {send.isError && <span className="text-blocked">{resendVerifyErrorText(status)}</span>}
    </div>
  )
}
